<template>
  <div class="usage-dashboard-chart" data-testid="usage-dashboard-chart-card">
    <div class="usage-dashboard-chart__header">
      <span class="usage-dashboard-chart__title heading-s">
        {{ chartTitle }}
      </span>

      <div class="usage-dashboard-chart__controls">
        <eg-dropdown
          ref="metricDropdown"
          :text="selectedMetricLabel"
          class="usage-dashboard-chart__metric-dropdown"
        >
          <eg-b-dropdown-item-button
            v-for="option in metricOptions"
            :key="option.value"
            :active="metric === option.value"
            @click="$emit('update:metric', option.value)"
          >
            {{ option.label }}
          </eg-b-dropdown-item-button>
        </eg-dropdown>

        <div class="usage-dashboard-chart__toggle">
          <eg-button
            size="small"
            :type="groupBy === 'total' ? 'primary' : 'ordinary'"
            @click="$emit('update:groupBy', 'total')"
          >
            Total
          </eg-button>
          <eg-button
            size="small"
            :type="groupBy === 'instructor' ? 'primary' : 'ordinary'"
            @click="$emit('update:groupBy', 'instructor')"
          >
            By instructor
          </eg-button>
        </div>
      </div>
    </div>

    <div class="usage-dashboard-chart__body">
      <infinity-loading v-if="isLoading && !hasData" />
      <template v-else-if="hasData">
        <chart-j-s-bar
          :chart-data="chartData"
          :chart-options="chartOptions"
          width="100%"
          :height="240"
        />
        <div v-if="isLoading" class="usage-dashboard-chart__overlay">
          <div class="usage-dashboard-chart__spinner" />
        </div>
      </template>
      <metrics-empty-state
        v-else
        title="No usage in this view"
        description="Try widening the date range or clearing the course/instructor filters."
      />
    </div>
  </div>
</template>

<script>
import ChartJSBar from "@/components/global/chartjs/chart_js_bar.vue";
import MetricsEmptyState from "@/views/metrics/components/shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import dropdownHideOnLeaveMixin from "@/mixins/dropdownHideOnLeaveMixin";

export default {
  name: "UsageDashboardChart",

  components: {
    ChartJSBar,
    MetricsEmptyState,
    InfinityLoading,
  },

  mixins: [dropdownHideOnLeaveMixin],

  props: {
    labels: {
      type: Array,
      default: () => [],
    },
    dataset: {
      type: Object,
      default: () => ({ label: "", data: [] }),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    metric: {
      type: String,
      default: "submissions",
    },
    groupBy: {
      type: String,
      default: "total",
    },
  },

  computed: {
    metricOptions() {
      return [
        { value: "submissions", label: "Submissions graded" },
        { value: "hoursSaved", label: "Hours saved" },
      ];
    },

    selectedMetricLabel() {
      return (
        this.metricOptions.find((o) => o.value === this.metric)?.label ?? ""
      );
    },

    chartTitle() {
      const base =
        this.metric === "hoursSaved" ? "Hours saved" : "Submissions graded";
      return this.groupBy === "instructor"
        ? `${base} by instructor`
        : `${base} by month`;
    },

    hasData() {
      return (
        this.labels.length > 0 &&
        (this.dataset.data || []).some((v) => v != null && v !== 0)
      );
    },

    chartData() {
      return {
        labels: this.labels,
        datasets: [
          {
            data: this.dataset.data,
            backgroundColor: "#7C3AED",
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      };
    },

    chartOptions() {
      const isHours = this.metric === "hoursSaved";
      const tickFont = {
        color: "#566370",
        font: { family: "Inter", size: 12, weight: "400", lineHeight: 1.2 },
      };
      return {
        responsive: true,
        maintainAspectRatio: false,
        onHover: (event, activeElements) => {
          event.native.target.style.cursor =
            activeElements.length > 0 ? "pointer" : "default";
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                isHours ? `${ctx.parsed.y} h saved` : `${ctx.parsed.y}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: tickFont,
          },
          y: {
            grid: { color: "#F0F3F6" },
            border: { display: false },
            min: 0,
            ticks: { ...tickFont, precision: 0 },
          },
        },
      };
    },
  },

  methods: {
    hideDropdowns() {
      this.$refs.metricDropdown?.hide();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../metrics/components/shared/metrics_card";

.usage-dashboard-chart {
  @include metrics-card;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;

  &__title {
    font-size: var(--typography-font-size-fs-600);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__metric-dropdown {
    @include metrics-dropdown;
    ::v-deep .btn {
      padding: 4px 10px;
    }
  }

  &__toggle {
    display: inline-flex;
    background: var(--color-surface-neutral-container-1, #f7f9fa);
    border-radius: var(--radius-rd-300, 8px);
    padding: 3px;
    gap: 2px;
  }

  &__body {
    min-height: 240px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    ::v-deep .chart-js {
      width: 100% !important;

      .chart {
        width: 100% !important;
      }
    }
  }

  &__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
  }

  &__spinner {
    width: 24px;
    height: 24px;
    border: 3px solid var(--color-stroke-neutral-default, #e5ebf0);
    border-top-color: var(--color-surface-primary-default, #8a3ffc);
    border-radius: 50%;
    animation: usage-dashboard-chart-spin 0.8s linear infinite;
  }
}

@keyframes usage-dashboard-chart-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
